import React from 'react';
import {
  LayoutDashboard,
  Gauge,
  Settings,
  GitCompare,
  Sliders,
  GitBranch,
  TrendingUp,
  Database,
  BarChart3,
  HelpCircle,
  FileText,
  ClipboardList,
  Users,
  ChevronLeft,
  ChevronRight,
  LogOut,
  X,
} from 'lucide-react';
import BitcoinIcon from '../shared/BitcoinIcon';
import { logout } from '../../lib/api';

// Ordered by call flow: verdict first, then the levers, then the paperwork.
const ADVISOR_NAV = [
  { id: 'plan', label: 'Plan Overview', icon: Gauge },
  { id: 'strategy-builder', label: 'Plan Configuration', icon: Sliders },
  { id: 'tax-economics', label: 'Tax & Economics', icon: Database },
  { id: 'market-settings', label: 'Market Settings', icon: Settings },
  { id: 'overview', label: 'Details', icon: LayoutDashboard },
  { id: 'benchmarks', label: 'Benchmarks', icon: TrendingUp },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'compare', label: 'Compare Plans', icon: GitBranch },
  { id: 'clients', label: 'Clients', icon: Users },
  { id: 'strategy-report', label: 'Strategy Report', icon: FileText },
  { id: 'cpa-report', label: 'CPA Report', icon: ClipboardList },
  { id: 'glossary', label: 'Glossary', icon: HelpCircle },
];

// Clients get a focused portal: the MinerOps plan as benchmark, their own
// options built from it, a comparison, and reports.
const CLIENT_NAV = [
  { id: 'plan', label: 'My Plan', icon: Gauge },
  { id: 'strategy-builder', label: 'Build an Option', icon: GitCompare },
  { id: 'compare', label: 'Compare Options', icon: GitBranch },
  { id: 'overview', label: 'Details', icon: LayoutDashboard },
  { id: 'strategy-report', label: 'My Report', icon: FileText },
  { id: 'glossary', label: 'Glossary', icon: HelpCircle },
];

const Sidebar = ({ activeSection, onSectionChange, collapsed, onToggle, isDrawer, onCloseDrawer, role = 'advisor' }) => {
  const navItems = role === 'client' ? CLIENT_NAV : ADVISOR_NAV;
  return (
    <aside
      style={{
        width: isDrawer ? 260 : (collapsed ? 64 : 240),
        minWidth: isDrawer ? 260 : (collapsed ? 64 : 240),
        height: '100vh',
        position: isDrawer ? 'relative' : 'fixed',
        left: isDrawer ? undefined : 0,
        top: isDrawer ? undefined : 0,
        background: '#FFFFFF',
        borderRight: '1px solid #E5E5E5',
        display: 'flex',
        flexDirection: 'column',
        transition: isDrawer ? 'none' : 'width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s cubic-bezier(0.4,0,0.2,1)',
        zIndex: 50,
        overflow: 'hidden',
        boxShadow: isDrawer ? '4px 0 24px rgba(0,0,0,0.12)' : 'none',
      }}
    >
      {/* Logo + close button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: collapsed && !isDrawer ? '20px 0' : '20px 20px',
          justifyContent: collapsed && !isDrawer ? 'center' : 'flex-start',
          borderBottom: '1px solid #E5E5E5',
          height: 68,
          flexShrink: 0,
          transition: 'padding 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div
          style={{
            background: '#1A1A1A',
            borderRadius: 4,
            padding: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BitcoinIcon style={{ width: 20, height: 20, color: '#FCFCFC' }} />
        </div>
        {(!collapsed || isDrawer) && (
          <span
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: '#1A1A1A',
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
              fontFamily: "'Outfit', sans-serif",
              flex: 1,
            }}
          >
            Miner<span style={{ color: '#92700C' }}>Ops</span>
          </span>
        )}
        {/* X close button in drawer mode */}
        {isDrawer && (
          <button
            onClick={onCloseDrawer}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 4,
              border: '1px solid #E5E5E5',
              background: '#F5F5F5',
              cursor: 'pointer',
              color: '#737373',
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '12px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {navItems.map((item) => {
          const isActive = activeSection === item.id;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              title={collapsed && !isDrawer ? item.label : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: (collapsed && !isDrawer) ? '10px 0' : '10px 20px',
                justifyContent: (collapsed && !isDrawer) ? 'center' : 'flex-start',
                background: isActive ? '#F5F5F5' : 'transparent',
                border: 'none',
                borderLeft: isActive ? '3px solid #92700C' : '3px solid transparent',
                cursor: 'pointer',
                transition: 'background 0.15s ease, border-color 0.15s ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.background = '#F5F5F5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isActive ? '#F5F5F5' : 'transparent';
              }}
            >
              <Icon
                size={20}
                style={{
                  color: isActive ? '#92700C' : '#737373',
                  flexShrink: 0,
                  transition: 'color 0.15s ease',
                }}
              />
              {(!collapsed || isDrawer) && (
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: isActive ? 500 : 400,
                    color: isActive ? '#1A1A1A' : '#525252',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    transition: 'color 0.15s ease',
                  }}
                >
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Logout + Collapse */}
      <div
        style={{
          borderTop: '1px solid #E5E5E5',
          padding: (collapsed && !isDrawer) ? '12px 0' : '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: (collapsed && !isDrawer) ? 'center' : 'space-between',
          flexShrink: 0,
          gap: 8,
          transition: 'padding 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <button
          onClick={logout}
          title="Sign out"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 4,
            border: '1px solid #E5E5E5',
            background: '#F5F5F5',
            cursor: 'pointer',
            color: '#737373',
            transition: 'background 0.15s ease, color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#FEF2F2';
            e.currentTarget.style.color = '#DC2626';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#F5F5F5';
            e.currentTarget.style.color = '#737373';
          }}
        >
          <LogOut size={14} />
        </button>
        {!isDrawer && (
          <button
            onClick={onToggle}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 4,
              border: '1px solid #E5E5E5',
              background: '#F5F5F5',
              cursor: 'pointer',
              color: '#737373',
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#E5E5E5';
              e.currentTarget.style.color = '#525252';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#F5F5F5';
              e.currentTarget.style.color = '#737373';
            }}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
